import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useFormValidation } from "../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import allImages from "../../assets/images-import";
import { handleErrors } from "../../utils/errorHandler";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../Common/FormElements/InputField";
import { TextareaField } from "../Common/FormElements/TextareaField";
import { ImageInputField } from "../Common/FormElements/ImageInputField";
import { TinyMCEEditorField } from "../Common/FormElements/TinyMCEEditorField";
import { fetchBlogById, updateBlog } from "../../services/blogService";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const UpdateBlog = () => {
  // Helper function to convert text to URL slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const { id } = useParams();

  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "blog/update/:id";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const blogNameRef = useRef(null);
  const blogURLRef = useRef(null);
  const postedOnRef = useRef(null);
  const postedByRef = useRef(null);
  const shortDescriptionRef = useRef(null);
  const fullDescriptionRef = useRef(null);
  const pageTitleRef = useRef(null);
  const metaDescriptionRef = useRef(null);
  const thumbImageInputRef = useRef(null);
  const detailImageInputRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    BlogName: "",
    BlogURL: "",
    PostedOn: "",
    PostedBy: "",
    PageTitle: "",
    MetaDescription: "",
    Published: false,
    FullDescription: "",
    ShortDescription: "",
    ThumbImage: null,
    DetailImage: null,
  });
  const [thumbImageUrl, setThumbImageUrl] = useState(
    allImages.ImagePlaceholder
  );
  const [detailImageUrl, setDetailImageUrl] = useState(
    allImages.ImagePlaceholder
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [content, setContent] = useState("");

  // ✅ Register all fields
  useEffect(() => {
    registerField("BlogName", blogNameRef, "Blog Name");
    registerField("PostedOn", postedOnRef, "Posted On");
    registerField("PostedBy", postedByRef, "Posted By");
    registerField("BlogURL", blogURLRef, "CategoBlogry URL");
    registerField("ShortDescription", shortDescriptionRef, "Short Description");
    registerField("FullDescription", fullDescriptionRef, "Full Description");
    registerField("PageTitle", pageTitleRef, "Page Title");
    registerField("MetaDescription", metaDescriptionRef, "Meta Description");
    registerField("ThumbImage", thumbImageInputRef, "Thumb Image");
    registerField("DetailImage", detailImageInputRef, "Detail Image");
  }, [registerField]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchBlogById(id);
        if (response && response.isSuccess && response.result) {
          setFormValues({
            Id: response.result.id,
            BlogName: response.result.blogName,
            BlogURL: response.result.blogURL,
            PostedOn: formatDate(response.result.postedOn),
            PostedBy: response.result.postedBy,
            PageTitle: response.result.pageTitle || "",
            MetaDescription: response.result.metaDescription || "",
            Published: response.result.published,
            FullDescription: response.result.fullDescription,
            ShortDescription: response.result.shortDescription,
            ThumbImage: null, // Reset file input
            DetailImage: null, // Reset file input
          });
          setContent(response.result.fullDescription);
          // Set thumb image URL if available
          if (response.result.thumbImageURL) {
            setThumbImageUrl(response.result.thumbImageURL);
          } else {
            setThumbImageUrl(allImages.ImagePlaceholder);
          }
          // Set detail image URL if available
          if (response.result.detailImageURL) {
            setDetailImageUrl(response.result.detailImageURL);
          } else {
            setDetailImageUrl(allImages.ImagePlaceholder);
          }
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, [id]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  // ✅ Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (thumbImageUrl && thumbImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbImageUrl);
      }
    };
  }, [thumbImageUrl]);

  useEffect(() => {
    return () => {
      if (detailImageUrl && detailImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(detailImageUrl);
      }
    };
  }, [detailImageUrl]);

  const handleEditorChange = (content) => {
    setContent(content);
    setFormValues((prevData) => ({
      ...prevData,
      FullDescription: content,
    }));
  };

  useEffect(() => {
    const dateInput = document.getElementById("PostedOn");

    if (dateInput) {
      const fp = flatpickr(dateInput, {
        dateFormat: "d-M-Y",
        monthSelectorType: "static",
        onChange: (selectedDates, dateStr) => {
          setFormValues((prevData) => ({ ...prevData, PostedOn: dateStr }));
        },
      });

      // Cleanup function to destroy the flatpickr instance
      return () => {
        if (fp) {
          fp.destroy();
        }
      };
    }
  }, []); // Add empty dependency array to run only once
  // ✅ Fix input handler for different input types
  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      // Auto-populate BlogURL when BlogName changes
      // Only auto-populate if URL field is empty or was previously auto-generated
      if (id === "BlogName") {
        // Check if URL field is empty or matches the slug of previous blog name
        const previousSlug = convertToSlug(prev.BlogName);
        const shouldAutoPopulate =
          !prev.BlogURL || prev.BlogURL === previousSlug;

        if (shouldAutoPopulate) {
          newValues.BlogURL = convertToSlug(inputValue);
        }
      }

      return newValues;
    });
  };

  const handleThumbImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (thumbImageUrl && thumbImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbImageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setThumbImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, ThumbImage: file }));
    }
  };

  const handleDetailImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (detailImageUrl && detailImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(detailImageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setDetailImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, DetailImage: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      var responseData = await updateBlog(formValues);

      // ✅ Reset all fields using refs
      // resetAllFields();

      // ✅ Complete form reset
      // setFormValues({
      //   BlogName: "",
      //   BlogURL: "",
      //   PageTitle: "",
      //   MetaDescription: "",
      //   Published: false,
      //   DisplayOrder: 1000,
      //   FullDescription: "",
      //   ShortDescription: "",
      //   ImageURLFile: null,
      // });

      // Clean up current image URL
      if (thumbImageUrl && thumbImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbImageUrl);
      }

      if (detailImageUrl && detailImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(detailImageUrl);
      }
      // setImageUrl(allImages.ImagePlaceholder);
      // setContent("");

      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Update Blog" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-8">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Update Blog</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Blog
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-12 col-md-6 col-sm-12">
                    <InputField
                      ref={blogNameRef}
                      label="Blog Name"
                      id="BlogName"
                      value={formValues.BlogName}
                      placeholder="Enter your Blog Name"
                      onChange={handleInputChange}
                      required={true}
                      // validationType="alphanumeric"
                      maxLength={300}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <InputField
                      ref={blogURLRef}
                      label="Blog URL"
                      id="BlogURL"
                      value={formValues.BlogURL}
                      placeholder="Auto generated URL"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="url"
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={postedByRef}
                      label="Posted By"
                      id="PostedBy"
                      value={formValues.PostedBy}
                      onChange={handleInputChange}
                      placeholder="Enter Posted By"
                      validationType="alphaonly"
                      maxLength={50}
                      required={true}
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={postedOnRef}
                      label="Posted On"
                      id="PostedOn"
                      value={formValues.PostedOn}
                      onChange={handleInputChange}
                      placeholder="Enter Posted On"
                      maxLength={50}
                      required={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TextareaField
                      ref={shortDescriptionRef}
                      label="Short Description"
                      id="ShortDescription"
                      value={formValues.ShortDescription}
                      onChange={handleInputChange}
                      placeholder="Enter Short Description"
                      required={true}
                      showCharCount={true}
                      maxLength={1500}
                    />
                  </div>
                  <div className="">
                    <TinyMCEEditorField
                      ref={fullDescriptionRef}
                      id="FullDescription"
                      label="Full Description"
                      content={content}
                      handleEditorChange={handleEditorChange}
                      required={true}
                    />
                  </div>
                </div>

                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Thumb Image <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <ImageInputField
                  ref={thumbImageInputRef}
                  id="ThumbImage"
                  imageUrl={thumbImageUrl}
                  onFileChange={handleThumbImageFileChange}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Detail Image <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <ImageInputField
                  ref={detailImageInputRef}
                  id="DetailImage"
                  imageUrl={detailImageUrl}
                  onFileChange={handleDetailImageFileChange}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">SEO Manager</h5>
              </div>
              <div className="card-body p-4">
                <TextareaField
                  ref={pageTitleRef}
                  label="Page Title"
                  id="PageTitle"
                  value={formValues.PageTitle}
                  onChange={handleInputChange}
                  placeholder="Enter Page Title"
                  showCharCount={true}
                  maxLength={300}
                  suggestedLength={70}
                />
                <TextareaField
                  ref={metaDescriptionRef}
                  label="Meta Description"
                  id="MetaDescription"
                  value={formValues.MetaDescription}
                  onChange={handleInputChange}
                  placeholder="Enter Meta Description"
                  showCharCount={true}
                  maxLength={500}
                  suggestedLength={160}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UpdateBlog;
